import { DEFAULT_TRAVEL_DURATION_MS } from "@/components/cursors/cursor-animation-types";
import {
  interactionCueDuration,
  PEN_TOUCH_DOWN_MS,
} from "./presentation-interaction-timing";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { abortableDelay } from "@/lib/abortable-delay";
import { ensureFrameTargetsVisible } from "@/lib/presentation-frame-targets";
import {
  acknowledgePresentationPaint,
  failPresentationStep,
} from "@/lib/agent-presentation-ack";
import {
  resolvePresentationDragPath,
  resolvePresentationRect,
} from "@/lib/agent-presentation-contract";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "@/lib/agent-presentation-types";
import { presentCursor } from "@/lib/canvas-cursor-presentation";
import {
  drawDurationMs,
  globalDrawPath,
  normalizedDrawPath,
  type DrawPoint,
} from "@/lib/presentation-draw-path";
import { animatePresentationPath } from "@/lib/presentation-path-animation";
import { waitForCursorAnimation } from "@/lib/cursor-animation-completion";
import { operateStepLabel } from "@/lib/presentation-step-label";

export function cursorContentForStep(
  step: AgentPresentationStep,
): string | undefined {
  if (step.kind === "pause" || step.kind === "clear") return undefined;
  const label = step.label?.trim();
  const bubble = step.bubble?.trim();
  return [label, bubble].filter(Boolean).join("\n\n") || operateStepLabel(step);
}
export function annotationCalloutForStep(
  step: AgentPresentationStep,
): string | undefined {
  return step.kind === "highlight" || step.kind === "draw"
    ? step.callout?.trim()
    : undefined;
}

function paceFactor(step: AgentPresentationStep): number {
  return step.kind !== "pause" && step.kind !== "clear" && step.pace === "quick"
    ? 0.72
    : step.kind !== "pause" && step.kind !== "clear" && step.pace === "slow"
      ? 1.35
      : 1;
}

export function travelDurationForStep(step: AgentPresentationStep): number {
  if (interactionCueDuration(step) !== null) return 0;
  if (
    step.kind === "pause" ||
    step.kind === "clear" ||
    step.transition?.style === "instant"
  )
    return 0;
  const defaultDuration =
    step.kind === "draw" || step.kind === "highlight" || step.kind === "drag"
      ? 500
      : DEFAULT_TRAVEL_DURATION_MS;
  return Math.round(
    (step.transition?.duration_ms ?? defaultDuration) * paceFactor(step),
  );
}

function pathBounds(points: DrawPoint[]) {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const width = Math.max(1, Math.max(...xs) - x);
  const height = Math.max(1, Math.max(...ys) - y);
  return { x, y, width, height };
}

async function showProgressivePath(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "draw" | "drag" }>,
  globalPath: DrawPoint[],
  normalizedPath: DrawPoint[],
  rect: { x: number; y: number; width: number; height: number },
  displayId: number | null,
  actionSeq: number,
  signal: AbortSignal,
): Promise<boolean> {
  const travelDurationMs = travelDurationForStep(step);
  const durationMs =
    step.transition?.style === "instant"
      ? 0
      : (step.transition?.duration_ms ?? drawDurationMs(globalPath, step.pace));
  const touchDownMs = durationMs > 0 ? PEN_TOUCH_DOWN_MS : 0;
  const windowDisplayId = getWindowDisplayId();
  if (
    displayId != null &&
    windowDisplayId != null &&
    displayId !== windowDisplayId
  ) {
    await abortableDelay(travelDurationMs + touchDownMs + durationMs, signal);
    return !signal.aborted;
  }
  const completionToken = `${presentation.presentationId}:${step.step_id}:${actionSeq}:start`;
  const common = {
    cursorLabel: cursorContentForStep(step),
    agentId: presentation.agentId,
    agentName: presentation.agentName,
    agentColor: presentation.agentColor,
    projectId: presentation.projectId,
    spaceId: presentation.spaceId,
    presentationDeliveryId: presentation.presentationId,
    executionId: presentation.executionId,
  };
  if (
    !presentCursor({
      ...common,
      cursorPoint: { ...globalPath[0]!, displayId },
      actionSeq,
      travelDurationMs,
      completionToken,
    })
  ) {
    await failPresentationStep(presentation, step, "cursor_space_unavailable");
    return false;
  }
  await waitForCursorAnimation(completionToken, signal, travelDurationMs + 250);
  if (touchDownMs > 0) await abortableDelay(touchDownMs, signal);
  if (signal.aborted) return false;
  const completed = await animatePresentationPath(
    normalizedPath,
    durationMs,
    signal,
    (frame) => {
      const tip = {
        x: rect.x + frame.tip.x * rect.width,
        y: rect.y + frame.tip.y * rect.height,
      };
      presentCursor({
        ...common,
        cursorPoint: { ...tip, displayId },
        annotation: {
          ...rect,
          displayId,
          shape: "path",
          path: frame.points,
          arrowEnd:
            (step.kind === "drag" ||
              step.shape === "arrow" ||
              step.user_action === "drag" ||
              step.arrow_end) &&
            frame.progress === 1,
          emphasis: step.kind === "draw" ? step.emphasis : undefined,
          strokeWidth: step.kind === "draw" ? step.stroke_width : undefined,
          padding: step.kind === "draw" ? step.padding : undefined,
          transition: "instant",
        },
        annotationId: `${presentation.presentationId}:${step.step_id}`,
        annotationCallout:
          frame.progress === 1 ? annotationCalloutForStep(step) : undefined,
        annotationCalloutRole:
          step.kind === "draw" ? step.callout_role : undefined,
        actionSeq: actionSeq * 10_000 + frame.frame,
        travelDurationMs: 0,
      });
    },
  );
  if (!completed || signal.aborted) return false;
  return acknowledgePresentationPaint(presentation, step, signal);
}

export async function showDraw(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "draw" }>,
  actionSeq: number,
  signal: AbortSignal,
): Promise<boolean> {
  const revealing = ensureFrameTargetsVisible(step, signal);
  if (revealing) {
    await revealing;
    if (signal.aborted) return false;
  }
  const rect = resolvePresentationRect(step, presentation);
  const normalized =
    rect &&
    normalizedDrawPath(
      step,
      rect,
      `${presentation.presentationId}:${step.step_id}`,
    );
  if (!rect || !normalized) {
    await failPresentationStep(
      presentation,
      step,
      "target_geometry_unresolved",
    );
    return false;
  }
  return showProgressivePath(
    presentation,
    step,
    globalDrawPath(normalized, rect),
    normalized,
    rect,
    rect.displayId,
    actionSeq,
    signal,
  );
}

export async function showDragPreview(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "drag" }>,
  actionSeq: number,
  signal: AbortSignal,
): Promise<boolean> {
  const revealing = ensureFrameTargetsVisible(step, signal);
  if (revealing) {
    await revealing;
    if (signal.aborted) return false;
  }
  const global = resolvePresentationDragPath(step, presentation);
  if (!global || global.length < 2) {
    await failPresentationStep(
      presentation,
      step,
      "target_geometry_unresolved",
    );
    return false;
  }
  const bounds = pathBounds(global);
  const normalized = global.map((point) => ({
    x: (point.x - bounds.x) / bounds.width,
    y: (point.y - bounds.y) / bounds.height,
  }));
  return showProgressivePath(
    presentation,
    step,
    global,
    normalized,
    bounds,
    global[0]!.displayId,
    actionSeq,
    signal,
  );
}
